/* global analytics */
import { createRouter, createWebHistory } from "vue-router";
import { resolveTourRoute } from "@/router/tourResolver";
import { helpRouteRecords } from "@/router/helpRoutes";
import { retiredAdminRouteRecords } from "@/router/retiredAdminRoutes";
import { retiredAuthRouteRecords } from "@/router/retiredAuthRoutes";
import { retiredMarketingRouteRecords } from "@/router/retiredMarketingRoutes";
import { rootRouteFate } from "@/router/rootRoute";
import { WORKSPACE_PATH } from "@/constants/workspacePath";
import { readSessionToken } from "@/services/sessionToken";
import { addressesGuestSurface, signedInLanding, startContextOf } from "@/router/guestOnlyRoutes";
import { isWorkspaceRoute } from "@/utils/workspaceRoute";
import { useAuthStore } from "@/stores/auth";
import { useUserStore } from "@/stores/user";
import { WORKSPACE_ONE_SHOT_PARAMS, useWorkspaceIntentStore } from "@/stores/workspaceIntent";

// `meta.surfaceTheme: "light"` marks a surface authored light-only (the legal
// center, public help):
// App.vue pins the resolved app theme to it while the route is mounted, so the
// page never renders under the dark document backdrop (th-989tg). Every other
// route follows the member's theme preference.
//
// `meta.workspace: true` marks every record that renders `MainView` — the
// workspace root, the `/tours/:id` placeholder, and the retired `/admin`
// placeholders. It is the structural "is this the workspace" test: the unified
// router writes `?state=` onto such a route in place, and lands a write made
// anywhere else on `MainMap` (`unifiedRouter.js#workspaceRouteTarget`), so a
// new public page is covered without editing any list.

/**
 * THE ONE `MainView` LOADER, shared by every record that renders the workspace
 * (the root, `/tours/:id` and the retired `/admin` placeholders) so they share
 * one chunk and one identity. Lazy because no module the entry reaches
 * statically imports a workspace module (launch-marketing-pages M2, the
 * boot-graph rule); a public page never loads the workspace.
 */
export const loadMainView = () => import("@/views/MainView.vue");

const router = createRouter({
  history: createWebHistory(),
  routes: [
    // `/why/:featureId` and `/solutions` are RETIRED (launch-marketing-pages
    // M1): each lands on Home without reading its params — see
    // `retiredMarketingRoutes.js`. Home is the placeholder, never rendered.
    ...retiredMarketingRouteRecords(() => import("@/views/public/HomePage.vue")),
    // The three public `/help` pages. Their records live in one module
    // because `auth.js` and `documentation.js` also ask "is this the public
    // help venue?" and must get the router's own answer (`helpRoutes.js`).
    ...helpRouteRecords,
    {
      path: "/legal",
      name: "LegalShell",
      component: () => import("@/views/LegalShell.vue"),
      meta: { surfaceTheme: "light" },
      children: [
        {
          path: "terms-of-service",
          name: "TermsOfService",
          component: () => import("@/components/legal/TermsOfService.vue"),
        },
        {
          path: "privacy-policy",
          name: "PrivacyPolicy",
          component: () => import("@/components/legal/PrivacyPolicy.vue"),
        },
        // The public bot policy page (artemis-fair-source-access D11). This
        // literal is the SECOND and LAST spelling of the path Artemis's user
        // agent advertises: the first is
        // `Artemis::SourceAccess::BOT_PAGE_PATH`, which a JavaScript router
        // cannot read, and whose comment names this file in return. Changing
        // the address is a two-file edit; anything else leaves the address we
        // publish to publishers and the address we serve disagreeing.
        //
        // No `meta`: the global guard keys on `meta.requiresAuth`, and a
        // policy page addressed to people who have never heard of us must
        // answer a signed-out visitor.
        {
          path: "artemis-bot",
          name: "ArtemisBotPolicy",
          component: () => import("@/components/legal/ArtemisBotPolicy.vue"),
        },
        {
          path: "",
          name: "LegalCenter",
          component: () => import("@/components/legal/LegalCenter.vue"),
        },
      ],
    },
    // The guest-only door. `guestOnly` is read by the guard below and by
    // `auth.js#signIn` through `router/guestOnlyRoutes.js`: a signed-in member
    // is sent to the workspace, never left on a form that renders nothing for
    // them.
    //
    // `/sign-in` and `/register` are one page, the sign-in door
    // (passwordless-sign-in D12): one door signs in and signs up. Both names
    // stay, because callers still push them.
    {
      path: "/sign-in",
      name: "SignIn",
      meta: { guestOnly: true },
      component: () => import("@/views/SignInDoorPage.vue"),
    },
    {
      path: "/register",
      name: "Register",
      meta: { guestOnly: true },
      component: () => import("@/views/SignInDoorPage.vue"),
    },
    // `/forgot-password`, `/password-resets/:token` and
    // `/email-verifications/:token` are RETIRED (passwordless-sign-in D12):
    // each raises the legacy-link notice and lands on the door, or the
    // workspace for a member, without reading its token — see
    // `retiredAuthRoutes.js`. The door page is the placeholder, never rendered.
    ...retiredAuthRouteRecords(() => import("@/views/SignInDoorPage.vue")),
    {
      // Legacy verify path kept as a redirect: outstanding emailed tokens still
      // point here until they expire / the 6-month verifier re-issues against the
      // v2 route. Preserves the :token param and any query (e.g. ?redirect).
      // (relationship-request-substrate D12)
      path: "/company-membership-verifications/:token",
      redirect: (to) => ({
        name: "VerifyRelationshipRequest",
        params: { token: to.params.token },
        query: to.query,
      }),
    },
    {
      path: "/relationship-requests/verify/:token",
      name: "VerifyRelationshipRequest",
      props: true,
      component: () => import("@/views/RelationshipRequestVerify.vue"),
    },
    {
      path: "/hubs/:token",
      name: "JoinDiscussionHub",
      props: true,
      component: () => import("@/views/JoinDiscussionHub.vue"),
    },
    // The share invitation email's link (waitlist-and-share-door W8): a guest
    // goes on to the door with the token, a signed-in member is asked before
    // the share converts. No `guestOnly` or `requiresAuth`: the page serves
    // both, as `JoinDiscussionHub` does.
    {
      path: "/shared/:token",
      name: "AcceptShare",
      props: true,
      component: () => import("@/views/AcceptSharePage.vue"),
    },
    {
      path: "/onboard-connected-account",
      component: () => import("@/views/OnboardConnectedAccountHandler.vue"),
      meta: { requiresAuth: true },
    },
    {
      path: "/onboard-shared-payout-account/:accountId",
      component: () => import("@/views/OnboardSharedPayoutAccountHandler.vue"),
      props: true,
      meta: { requiresAuth: true },
    },
    {
      path: "/tours/:id",
      name: "TourRedirect",
      meta: { requiresAuth: true, workspace: true },
      // No dedicated component — this is a pure redirect route.
      // beforeEnter fetches the tour and opens it in the reader, or for a tour
      // the reader does not play, resolves its topic and navigates there.
      beforeEnter: resolveTourRoute,
      // Placeholder component is never rendered (guard always redirects)
      component: loadMainView,
    },
    // `/admin` and its four children are RETIRED (economics-pulse D12). Each is
    // now a redirect into Analyze > Pulse with the matching drawer — see
    // `retiredAdminRoutes.js` for the table and why they resolve through a
    // guard rather than a bare `redirect:`. No dedicated component: the
    // placeholder is never rendered.
    ...retiredAdminRouteRecords(loadMainView),
    // THE PUBLIC PAGES (launch-marketing-pages M1). `/` is Home; the layout is
    // the parent's, so a page never repeats it. The root fate is the `Home`
    // CHILD's `beforeEnter`, never the parent's, so a member's entry at
    // `/owners` renders the Owner page (`router/rootRoute.js`).
    {
      path: "/",
      component: () => import("@/views/public/PublicLayout.vue"),
      meta: { public: true },
      children: [
        {
          path: "",
          name: "Home",
          component: () => import("@/views/public/HomePage.vue"),
          beforeEnter: (to, from) => rootRouteFate(to, from, readSessionToken() != null),
        },
        {
          path: "owners",
          name: "OwnerPage",
          component: () => import("@/views/public/OwnerPage.vue"),
        },
        {
          path: "showcase",
          name: "Showcase",
          component: () => import("@/views/public/ShowcasePage.vue"),
        },
      ],
    },
    // THE SHOWCASE TOUR'S HAND-OFF (launch-marketing-pages M9): "Take the
    // tour" on `/showcase` lands here. The guard writes the tour into the
    // workspace intent, which no URL carries, and sends the visitor on to the
    // workspace, where `WorkspaceShell` takes it once and plays it. No
    // component: the guard always redirects. Not `meta.public`, so the page
    // budget never counts it; its meta is server-side. The global guard has
    // already dropped the intent for this non-workspace route, so this write is
    // the last thing to touch it before the workspace, and the redirect is
    // workspace-bound, which keeps it. The location names only `MainMap`, so
    // neither the key nor any query rides on into the workspace.
    {
      path: "/showcase/:key",
      name: "ShowcaseTour",
      beforeEnter: (to) => {
        useWorkspaceIntentStore().setTour({ showcase: to.params.key });
        return { name: "MainMap" };
      },
    },
    {
      path: WORKSPACE_PATH,
      component: loadMainView,
      meta: { workspace: true },
      children: [
        {
          path: "",
          name: "MainMap",
          component: () => import("@/components/maps/MainMap.vue"),
        },
      ],
    },
  ],
});

/**
 * THE ONE ROUTE GUARD: an unmatched path goes to Home with its query and hash
 * (where the root fate forwards a workspace address on), a guest-only surface
 * admits only a guest, a workspace address hands its one-shot params to the
 * workspace intent, and a route that declares `requiresAuth` needs a token.
 *
 * THE GUEST-ONLY ARM (th-49c64). A route that declares `guestOnly`, or any
 * route that addresses the workspace's Authenticate panel through the flat
 * `?horizontalTab=Authenticate`, sends a signed-in member to
 * `signedInLanding` (`router/guestOnlyRoutes.js`), resolved from a start
 * context captured off the address. That is the one surface that accepts the
 * invitation the address carries, its same-origin `?redirect=`, or the
 * workspace. The forms
 * keep no exit of their own, so a bookmark, the Back button, a second tab, the
 * post-sign-in re-boot and a view's own layout-crossing push all resolve here,
 * in one place. A guest holds no bearer and passes at once, without waiting. A
 * held bearer is decided on the RESOLVED identity (`whenIdentityResolved`),
 * never on the default guest that `currentUser` holds before whoami answers.
 * App.vue renders the RouterView only once the identity resolves, so the wait
 * shows nothing new.
 *
 * THERE IS NO `requiresAdmin` ARM (economics-pulse D12, th-l19q2.25). Exactly
 * one route ever declared that meta key — `/admin` — and it is retired into
 * Pulse, so the arm had no live route left to gate. It carried two things that
 * went with it:
 *
 *   - A `localStorage` admin-bearer DISJUNCT beside the server's `whoami`. That
 *     is a client-held bypass of a server fact: the key held an admin's own
 *     bearer independent of whoever the session had since become, so it kept
 *     admin routing alive past an identity change the server had already
 *     acted on. Admin surfaces are now Pulse sections, gated by the SERVER-
 *     stamped `pulse` capability (`Web::Session#pulse?`) and by `AdminGated`
 *     on every read behind them.
 *   - An unsaved-changes confirm for a `DataInputJobsAdmin` route. That route
 *     went with the v1 easy-button data-input admin (661ec0675); the confirm
 *     outlived it, keyed on a `from.name` no record has answered to since, and
 *     on a `globalUnsaved` no live component populates.
 */
router.beforeEach((to) => {
  if (to.matched.length === 0) {
    console.log("unmatched route", to.fullPath);
    return { path: "/", query: to.query, hash: to.hash };
  }

  if (addressesGuestSurface(to)) return admitGuestOnly(to);

  const intentStripped = settleWorkspaceIntent(to);
  if (intentStripped) return intentStripped;

  if (!to.meta.requiresAuth) return true;

  if (readSessionToken() == null) {
    return { name: "SignIn", query: { redirect: to.fullPath } };
  }

  return true;
});

/**
 * THE ONE PAGE-VIEW AND TITLE HOOK (launch-marketing-pages M13), after every
 * confirmed navigation, guests included:
 *
 * - A PAGE VIEW is recorded when the path or the route name changes, so a
 *   `?state=` write or a one-shot strip, which replaces the query in place,
 *   records none. `analytics` is `null` off production
 *   (`application.html.erb`), and no consent mechanism exists.
 * - THE TITLE is "Tower Hunt" on every route that is not `meta.public`. A
 *   public page sets its own from its page file on mount, so the router
 *   imports no page file; the server's `<title>` covers first paint.
 */
router.afterEach((to, from, failure) => {
  if (failure) return;

  if (!to.meta.public) document.title = "Tower Hunt";

  if (analytics && (to.path !== from.path || to.name !== from.name)) {
    analytics.page({ path: to.path });
  }
});

/**
 * The guest-only arm: let a guest through, and send a signed-in member to
 * `signedInLanding`.
 */
async function admitGuestOnly(to) {
  if (readSessionToken() == null) return true;

  await useAuthStore().whenIdentityResolved();

  return useUserStore().signedIn ? signedInLanding(startContextOf(to)).location : true;
}

/**
 * The workspace-intent arm (launch-marketing-pages M4): a workspace address
 * hands every `WORKSPACE_ONE_SHOT_PARAMS` key it carries to the workspace
 * intent and is answered with the same location without them; every other
 * route this arm sees drops what the intent still holds. Answers null when the
 * navigation passes unchanged.
 *
 * - ONE NAVIGATION. The stripped location is the guard's answer, never a
 *   `router.replace` of its own, so the address that carried the keys is never
 *   committed and nothing races a later URL write. The answer inherits the
 *   navigation's own mode: an entry replaces, as every first navigation does,
 *   and an in-app push stays a push, so Back from a Home search returns to
 *   Home. A same-route `?q` resolves to the route already showing, so the
 *   address does not move and the intent still takes the search.
 * - THE LIFETIME. Only a route that is not a workspace route drops the
 *   intent. A redirect into the workspace and the unified router's arrival
 *   `?state=` write are workspace to workspace, so a tour a record wrote is
 *   still held when the lazy shell mounts.
 * - AFTER THE GUEST-ONLY ARM, so a door address reaches the door's start
 *   context whole, and the door's own routes leave the intent alone. No writer
 *   pairs a one-shot key with the door, and a sign-in re-boots the page, which
 *   starts with an empty intent.
 */
function settleWorkspaceIntent(to) {
  const intent = useWorkspaceIntentStore();

  if (!isWorkspaceRoute(to)) {
    intent.clear();
    return null;
  }

  const query = to.query ?? {};
  const rest = Object.fromEntries(
    Object.entries(query).filter(([key]) => !WORKSPACE_ONE_SHOT_PARAMS.includes(key)),
  );
  if (Object.keys(rest).length === Object.keys(query).length) return null;

  intent.put(query);
  return { ...to, query: rest };
}

export default router;
